import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { noop } from 'Helpers/PropTypes';
import {
  Box, makeStyles, Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { QueryVal, addQuery } from 'Helpers/Query';
import useCollection from 'Hooks/useCollection';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import Button from 'Components/UI/Button';
import fr from 'facet-rest';
import { useLayoutContext } from 'Context/LayoutContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import Agenda from './Agenda';
import ActionItemSection from './ActionItemSection';
import MeetingHeader from './MeetingHeader';

const useStyles = makeStyles(theme => ({
  rootDesktop: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    flexBasis: 0,
    flexShrink: 1,
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    maxWidth: 'none',
    minHeight: `calc(100vh - ${theme.heights.advisorNavigationHeight}px)`,
    position: 'relative',
  },
  rootMobile: {},
  meetingDetails: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(0, 2, 0, 2),
    },
  },
  meetingTitle: {
    marginBottom: theme.spacing(2),
  },
  meetingDiscussion: {
    flexBasis: '40%',
    height: theme.dimensions.md,
  },
  actionItemHeader: {
    margin: theme.spacing(6, 0, 2),
  },
  meetingNav: {
    display: 'flex',
    marginBottom: '10px',
  },
  noMeeting: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexFlow: 'column',
    height: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    margin: 'auto',
  },
  scheduleButton: {
    marginTop: theme.spacing(2),
  },
  spacer: {
    flexGrow: 1,
  },
  meetingNavButton: {
    flexGrow: 1,
  },
  navChevron: {
    color: theme.palette.facet.primaryGray,
    fontSize: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    meetingDetails: {
      display: 'flex',
      flexFlow: 'column',
      '& > *': {
        flexBasis: '100%',
        width: '100%',
        margin: theme.spacing(0, 0, 1, 0),
      },
    },
    noMeeting: {
      position: 'relative',
      height: `calc(100vh - ${theme.heights.clientNavigationHeight}px)`,
    },
  },
  panelParent: {
    display: 'flex',
    flexDirection: 'row',
  },
  leftPanel: {
    padding: theme.spacing(0, 2),
    width: '100%',
  },
  rightPanel: {
    width: 500,
    minWidth: 500,
  },
}));

export default function Meeting({
  householdResource,
  leadPlannerResource,
  todosDisabled,
  preview,
  onLoadFailure,
}) {
  const classes = useStyles();
  const [loading, setLoading] = React.useState(true);
  const location = useLocation();
  const { setFooterVisibility } = useLayoutContext();
  const isMobile = useMobileLayout();

  useEffect(() => {
    setFooterVisibility(false);
    return () => setFooterVisibility(true);
  }, []);

  const {
    collection: meetingCollection,
    setCollection: setMeetingCollection,
  } = useCollection();

  const {
    collection: clientActionItems,
    setCollection: setClientActionItems,
  } = useCollection();

  const {
    collection: plannerActionItems,
    setCollection: setPlannerActionItems,
  } = useCollection();

  const id = QueryVal('meetingId');

  function getActionItems() {
    if (
      meetingCollection
      && meetingCollection
        .resources()
        .head()
        .relationship(fr.relationships.incompleteClientActionItems.name)
    ) {
      meetingCollection
        .resources()
        .head()
        .relationship(fr.relationships.incompleteClientActionItems.name)
        .link('related')
        .Get({ params: { hidden: 'false', notWorkflow: 'true' } })
        .then((col) => {
          // If we get a redirect we will create a collection w/o a self link - See FW-2582
          if (col && col.link('self')) {
            return col.fetchAllPages();
          }
          return null;
        })
        .then((fullCol) => {
          if (fullCol) {
            setClientActionItems(fullCol);
          }
        });
    }
    if (
      meetingCollection
      && meetingCollection
        .resources()
        .head()
        .relationship('incompletePlannerActionItems')
    ) {
      meetingCollection
        .resources()
        .head()
        .relationship('incompletePlannerActionItems')
        .link('related')
        .Get({ params: { hidden: 'false', notWorkflow: 'true' } })
        .then((col) => {
          // If we get a redirect we will create a collection w/o a self link - See FW-2582
          if (col && col.link('self')) {
            return col.fetchAllPages();
          }
          return null;
        })
        .then((fullCol) => {
          if (fullCol) {
            setPlannerActionItems(fullCol);
          }
        });
    }
  }

  const replaceClientActionItem = (resource) => {
    clientActionItems.resources().updateItem(resource);
    setClientActionItems(clientActionItems);
  };

  const refreshActionItems = () => {
    return Promise.all([clientActionItems?.refresh().then(next => setClientActionItems(next), plannerActionItems?.refresh().then(next => setClientActionItems(next)))]);
  };

  useEffect(() => {
    if (
      id
      && householdResource
      && householdResource.relationship('meetings')
      && householdResource.relationship('meetings').link('related')
    ) {
      householdResource
        .relationship('meetings')
        .link('related')
        .Get({ params: { id, prefill: preview } })
        .then((meetingResponse) => {
          setLoading(false);
          setMeetingCollection(meetingResponse);
        })
        .catch((err) => {
          displayErrorMessage(err, err.errors);
          onLoadFailure();
        });
    }
  }, [householdResource, id]);

  function updateMeetingQueryParam(coll) {
    if (coll.resources().length() > 0) {
      addQuery({
        meetingId: coll
          .resources()
          .head()
          .id(),
      });
    }
  }

  const loadPreviousMeetings = () => {
    setLoading(true);
    meetingCollection
      .link('prev')
      .Get()
      .then((coll) => {
        setLoading(false);
        setMeetingCollection(coll);
        updateMeetingQueryParam(coll);
      });
  };

  const loadNextMeetings = () => {
    setLoading(true);
    meetingCollection
      .link('next')
      .Get()
      .then((coll) => {
        setLoading(false);
        setMeetingCollection(coll);
        updateMeetingQueryParam(coll);
      });
  };

  useEffect(() => {
    getActionItems();
  }, [meetingCollection, location.search]);

  if (!id) {
    return (
      <div className={classes.noMeeting}>
        <Typography variant="body" color="primary">
          You do not have a meeting scheduled
        </Typography>
        <Button
          className={classes.scheduleButton}
          variant="contained"
          color="secondary"
          href={leadPlannerResource?.attr('calendarURL')}
          target="_blank"
        >
          Schedule your next meeting
        </Button>
      </div>
    );
  }


  if (!meetingCollection || !meetingCollection.isValid() || loading) {
    return (
      <Box className={isMobile ? classes.rootMobile : classes.rootDesktop}>
        <LoadingOverlay />
      </Box>
    );
  }

  const nextLink = meetingCollection.link('next');
  const prevLink = meetingCollection.link('prev');

  const [meeting] = meetingCollection;

  return (
    <div className={isMobile ? classes.rootMobile : classes.rootDesktop} {...dataAttrs('meeting-preview')}>
      <div className={classes.leftPanel}>
        <MeetingHeader meeting={meeting} />
        <div className={classes.meetingNav}>
          {prevLink && (
            <Button onClick={() => loadPreviousMeetings()}>
              <ChevronLeft className={classes.navChevron} />
              <Typography variant="h5" color="primary">
                Previous Meeting
              </Typography>
            </Button>
          )}
          <span className={classes.spacer} />
          {nextLink && (
            <Button onClick={() => loadNextMeetings()}>
              <Typography variant="h5" color="primary">
                Next Meeting
              </Typography>
              <ChevronRight className={classes.navChevron} />
            </Button>
          )}
        </div>
        <div className={classes.leftPanel}>
          <div className={classes.meetingDetails}>
            <Agenda meeting={meeting} />
          </div>
          {clientActionItems && (
            <ActionItemSection
              assignedToHousehold
              actionItemCollection={clientActionItems}
              refreshActionItems={refreshActionItems}
              replaceActionItem={replaceClientActionItem}
              readOnly={todosDisabled}
            />
          )}
        </div>
      </div>
    </div>
  );
}

Meeting.defaultProps = {
  todosDisabled: false,
  preview: false,
  onLoadFailure: noop,
};

Meeting.propTypes = {
  householdResource: PropTypes.object.isRequired,
  leadPlannerResource: PropTypes.object.isRequired,
  todosDisabled: PropTypes.bool,
  preview: PropTypes.bool,
  onLoadFailure: PropTypes.func,
};
